import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { imgUrl } from "@/lib/format";

const IMAGES = {
  "Bilezik": "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxnb2xkJTIwYnJhY2VsZXQlMjBqZXdlbHJ5fGVufDB8fHx8MTc4NjQwMzY0M3ww&ixlib=rb-4.1.0&q=85",
  "Kolye": "https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxnb2xkJTIwbmVja2xhY2UlMjBqZXdlbHJ5fGVufDB8fHx8MTc4NjQwMzY0M3ww&ixlib=rb-4.1.0&q=85",
  "Küpe": "https://images.unsplash.com/photo-1651160670627-2896ddf7822f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxnb2xkJTIwZWFycmluZ3MlMjBqZXdlbHJ5fGVufDB8fHx8MTc4NjQwMzY0M3ww&ixlib=rb-4.1.0&q=85",
  "Yüzük": "https://images.unsplash.com/photo-1611955167811-4711904bb9f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxnb2xkJTIwcmluZyUyMGpld2Vscnl8ZW58MHx8fHwxNzg2NDAzNjQzfDA&ixlib=rb-4.1.0&q=85",
  "Alyans": "https://images.unsplash.com/photo-1589207212797-cfd546dea0fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxnb2xkJTIwcmluZyUyMGpld2Vscnl8ZW58MHx8fHwxNzg2NDAzNjQzfDA&ixlib=rb-4.1.0&q=85",
  "Zincir": "https://images.unsplash.com/photo-1633810543462-77c4a3b13f07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYnJhY2VsZXQlMjBqZXdlbHJ5fGVufDB8fHx8MTc4NjQwMzY0M3ww&ixlib=rb-4.1.0&q=85",
};
const FALLBACK = "https://images.unsplash.com/photo-1601121141418-c1caa10a2a0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxnb2xkJTIwbmVja2xhY2UlMjBqZXdlbHJ5fGVufDB8fHx8MTc4NjQwMzU1MHww&ixlib=rb-4.1.0&q=85";

export default function Categories() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).finally(() => setLoading(false));
  }, []);

  const goto = (cat, sub) => {
    const base = `/category/${encodeURIComponent(cat)}`;
    navigate(sub ? `${base}?sub=${encodeURIComponent(sub)}` : base);
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>;

  return (
    <div>
      <h1 className="font-display text-3xl text-foreground mb-1">Kategoriler</h1>
      <p className="text-sm text-muted-foreground mb-6">Koleksiyonları kategoriye göre keşfedin.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {categories.map((c, i) => {
          const subs = c.subcategories || [];
          return (
            <div key={c.id} className="flex flex-col animate-fade-up" style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}>
              <button
                data-testid={`category-tile-${c.name}`}
                onClick={() => goto(c.name)}
                className="group relative rounded-md overflow-hidden border border-border aspect-[3/2]"
              >
                <img src={c.image_url ? imgUrl(c.image_url) : (IMAGES[c.name] || FALLBACK)} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between">
                  <span className="text-white font-medium text-sm">{c.name}</span>
                  <ChevronRight size={16} className="text-gold" />
                </div>
              </button>

              {subs.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`category-subcards-${c.name}`}>
                  {subs.map((s) => (
                    <button
                      key={s}
                      data-testid={`category-subcard-${c.name}-${s}`}
                      onClick={() => goto(c.name, s)}
                      className="px-2.5 py-1 rounded-md text-[11px] bg-surface border border-border text-muted-foreground hover:border-gold hover:text-gold transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
